import { Link, NavLink, Outlet, matchPath, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  BarChart3,
  Bot,
  Building2,
  CalendarClock,
  FileAudio,
  LayoutDashboard,
  ListTodo,
  LogOut,
  Phone,
  Receipt,
  RefreshCw,
  ScrollText,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import { api, type Area, type Company, type Page } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Combobox, Spinner, type ComboboxItem } from "@/components/ui";
import { useAuth } from "./auth";

type NavEntry = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  /** Nur genau dieser Pfad ist aktiv, nicht alles darunter. */
  end?: boolean;
  /** Folgt dem Firmenumschalter in der Kopfzeile. */
  scoped?: boolean;
  /** Gehört dem Betreiber; nur sichtbar, wenn die Rolle den Bereich darf. */
  area?: Area;
};

// The navigation is grouped the way the work is: who exists, what they can
// announce, what is running, and what needs looking at.
//
// `scoped` marks the pages that show one company's data and therefore follow
// the switcher in the header. The others — the overview, the company
// catalogue itself, the system-wide definitions, the robot, billing, sync,
// system — do not, so the switcher stays hidden there rather than pretending
// to steer them.
//
// `area` marks the pages that belong to the operator rather than to a
// customer. A firm's own administrator does not get them: the entry is left
// out here, the route refuses in the router, and the endpoints behind it
// refuse in the server. Three places, because a menu that merely hides
// something is decoration, not a permission.
const nav: NavEntry[] = [
  { to: "/", label: "Übersicht", icon: LayoutDashboard, end: true },
  { to: "/companies", label: "Firmen", icon: Building2 },
  { to: "/users", label: "Benutzer", icon: Users, scoped: true },
  { to: "/announcements", label: "Ansagen", icon: CalendarClock, scoped: true },
  { to: "/definitions", label: "Definitionen", icon: FileAudio, area: "definitions" },
  { to: "/jobs", label: "Hintergrundarbeit", icon: ListTodo, scoped: true },
  { to: "/robot", label: "Roboter", icon: Phone, area: "robot" },
  { to: "/hybrid", label: "Hybrid-Roboter", icon: Bot, area: "robot" },
  { to: "/billing", label: "Abrechnung", icon: Receipt, area: "billing" },
  { to: "/sync", label: "Synchronisation", icon: RefreshCw, area: "sync" },
  { to: "/stats", label: "Statistiken", icon: BarChart3, scoped: true },
  { to: "/logs", label: "Protokolle", icon: ScrollText, scoped: true },
  { to: "/alerts", label: "Meldungen", icon: AlertTriangle, scoped: true, area: "alerts" },
  { to: "/system", label: "System", icon: Settings, area: "system" },
];

/** Whether the page at `pathname` shows one company's data. */
function isScopedPage(pathname: string): boolean {
  return nav.some(
    (n) => n.scoped && matchPath({ path: n.to, end: n.end ?? false }, pathname),
  );
}

export function AppLayout() {
  const { me, loading, signOut } = useAuth();
  const navigate = useNavigate();

  if (loading) return <Spinner label="Anmeldung wird geprüft" />;
  if (!me) return null; // the router sends us to the sign-in page

  return (
    <div className="flex h-full">
      <aside
        className="hidden w-52 shrink-0 flex-col border-r sm:flex"
        style={{ borderColor: "var(--border)", background: "var(--surface-sunken)" }}
      >
        <div className="flex h-12 items-center gap-2.5 px-3.5">
          <span
            className="grid size-7 shrink-0 place-items-center rounded-md bg-brand-600 text-xs font-bold text-white shadow-xs"
            aria-hidden
          >
            M
          </span>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold tracking-tight">Mobilboxmanager</div>
            <div className="text-[0.6875rem] muted">Verwaltung</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 pt-1">
          {nav
            .filter((n) => !n.area || (me.areas ?? []).includes(n.area))
            .map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "mb-px flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-brand-600/10 text-brand-700 dark:bg-brand-500/20 dark:text-brand-100"
                      : "muted hover:bg-[var(--surface-raised)] hover:text-[var(--text)]",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className={cn("size-4 shrink-0", !isActive && "opacity-70")}
                      aria-hidden
                    />
                    {label}
                  </>
                )}
              </NavLink>
            ))}
        </nav>

        <div
          className="flex items-center gap-2.5 border-t px-3.5 py-2.5"
          style={{ borderColor: "var(--border)" }}
        >
          <span
            className="grid size-7 shrink-0 place-items-center rounded-full bg-[var(--surface-raised)] text-[0.6875rem] font-semibold ring-1 ring-inset ring-[var(--border)]"
            aria-hidden
          >
            {initials(me.name)}
          </span>
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-medium">{me.name}</div>
            <div className="truncate text-[0.6875rem] muted">{roleLabel(me.role)}</div>
          </div>
          {/* Das eigene Konto, nicht das System: der zweite Faktor und die
              Wiederherstellungscodes gehören neben den eigenen Namen und nicht
              in die Liste der Verwaltungsseiten. */}
          <Link
            to="/security"
            className="grid size-7 shrink-0 place-items-center rounded-md muted hover:bg-[var(--surface-raised)] hover:text-[var(--text)]"
            title="Sicherheit: zweiter Faktor"
            aria-label="Sicherheit"
          >
            <ShieldCheck className="size-4" aria-hidden />
          </Link>
          <button
            className="grid size-7 shrink-0 place-items-center rounded-md muted hover:bg-[var(--surface-raised)] hover:text-[var(--text)]"
            title="abmelden"
            aria-label="abmelden"
            onClick={async () => {
              await signOut();
              navigate("/signin");
            }}
          >
            <LogOut className="size-4" aria-hidden />
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar />
        <main className="min-w-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** The company switcher replaces the value the old portal kept in the session. */
function TopBar() {
  const { me, setScope } = useAuth();
  const { pathname } = useLocation();
  const scoped = isScopedPage(pathname);
  // Every company the person may administer, in one request: a switcher that
  // silently stops at the two-hundredth company hides the rest of them.
  const { data } = useQuery({
    queryKey: ["companies", "all"],
    queryFn: () => api<Page<Company>>("/api/admin/companies?limit=1000"),
  });

  const companies = data?.items ?? [];
  const isGlobal = me?.role === "global_admin";

  // Names alone do not separate look-alike companies, so the domain rides
  // along: it is shown under each name and it is searched with it.
  const items: ComboboxItem[] = [
    ...(isGlobal ? [{ value: 0, label: "alle Firmen" }] : []),
    ...companies.map((c) => ({ value: c.id, label: c.name, hint: c.domain })),
  ];

  return (
    <header
      className="flex h-12 shrink-0 items-center justify-between gap-4 border-b px-4 sm:px-6"
      style={{ borderColor: "var(--border)", background: "var(--surface-raised)" }}
    >
      {scoped ? (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs font-medium muted">Firma</span>
          <Combobox
            className="w-60"
            label="Firma"
            placeholder="Firma wählen"
            searchPlaceholder="Firma suchen …"
            emptyLabel="keine Firma gefunden"
            items={items}
            value={me?.scopeCompanyId ?? 0}
            onChange={(v) => void setScope(Number(v))}
          />
        </div>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium muted">
          <span className="size-1.5 rounded-full bg-brand-500" aria-hidden />
          systemweit
        </span>
      )}

      <span className="text-xs muted sm:hidden">{me?.name}</span>
    </header>
  );
}

/** "Vorname Nachname" → "VN"; a single word gives its first letter. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

function roleLabel(role: string): string {
  switch (role) {
    case "global_admin":
      return "Globaler Admin";
    case "self_admin":
      return "Mehrfirmen-Admin";
    case "company_admin":
      return "Firmenadmin";
    default:
      return role;
  }
}
